import React, { Dispatch, Fragment, SetStateAction } from 'react';

import { Column } from '../../NewStrategy/StrategyTable';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { ProfitLoss } from '../../ProfitLoss';
import { Sort } from '../../NewStrategy/Sort';

import { useSort } from '../../../hooks/useSort';

import { Center, TimeFrame } from '../types';
import { Currency } from '../../../types/common';
import { SortType } from '../../NewStrategy/types';
import { BarData, Bond } from '.';
import { ColorButton, Icon } from '../../UI';
import { COLORS_HEX } from '../../../constants/colors';
import { CurrencySymbolEnum } from '../../../constants/enums';

const Container = ({
  fitMinWidth,
  id,
  center,
  color,
  columnName,
  decimalScale,
  isColumn,
  symbol,
  percentage,
  value,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  center: Center;
  color?: string;
  columnName: string;
  decimalScale?: number;
  id: string;
  isColumn: boolean;
  fitMinWidth: boolean;
  symbol?: CurrencySymbolEnum;
  percentage?: number;
  value: number | string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}) => {
  return (
    <div
      className={`
        ${fitMinWidth ? 'w-fit' : ' min-w-32'}
        flex
        items-center
        pl-5
        py-2
        w-full
        ${
          center.id === id
            ? isColumn
              ? columnName === 'ISSUER'
                ? 'border-l border-t border-r'
                : ['CURRENCY', 'Capital'].includes(columnName)
                ? 'border-l border-b'
                : ['BOND TYPE', 'Flow'].includes(columnName)
                ? 'border-r border-b'
                : ['VALUE', 'Coupon'].includes(columnName)
                ? 'border-r'
                : 'border-l'
              : columnName === 'ISSUER'
              ? 'border-b border-t border-l'
              : 'border-b border-t'
            : !isColumn && 'border-b border-grey-10 dark:border-grey-70'
        }
        ${
          columnName === 'ISSUER' &&
          isColumn &&
          'col-span-2 py-3 cursor-pointer'
        }
      `}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <span className="flex flex-col h-full justify-center">
        {isColumn && (
          <span className="text-grey-50 font-light">{columnName}</span>
        )}
        {typeof value === 'number' ? (
          <CustomNumber
            className={'cursor-default w-full'}
            suffix={symbol}
            value={value}
            decimalScale={decimalScale}
          />
        ) : (
          <div className="flex items-center gap-2">
            {color && (
              <ColorButton
                radius={3}
                color={
                  Object.entries(COLORS_HEX).find(
                    ([_, col]) => col === color
                  )![0]
                }
                isReadOnly
              />
            )}
            <div>{value}</div>
          </div>
        )}
        {percentage && <ProfitLoss percentage={percentage} symbol={symbol} />}
      </span>
    </div>
  );
};

export const GroupsList = ({
  center,
  columns,
  column = false,
  fitMinWidth = false,
  tableData,
  interval,
  symbol,
  viewType,
  widgetWidth,
  resetCenter,
  setCenter,
  setViewType,
}: {
  center: Center;
  columns: SortType[];
  column?: boolean;
  fitMinWidth?: boolean;
  tableData: (BarData & Bond)[];
  interval?: TimeFrame;
  symbol: Currency['symbol'];
  viewType: (number | 'CAPITAL' | 'COUPON')[];
  widgetWidth: number;
  resetCenter(): void;
  setCenter: React.Dispatch<React.SetStateAction<Center>>;
  setViewType: Dispatch<SetStateAction<(number | 'CAPITAL' | 'COUPON')[]>>;
}) => {
  const { type, handleSort } = useSort(columns);

  const onMouseLeave = () => resetCenter();

  return (
    <>
      {viewType.length > 0 && (
        <div className="flex items-center gap-3 ml-5 py-2">
          <Icon
            className="cursor-pointer"
            svgName="arrowBack"
            onClick={() => setViewType((pr) => pr.slice(0, -1))}
          />
          <span>{viewType.slice(-1)[0]}</span>
        </div>
      )}
      <div
        className={`
          w-full
          min-w-${widgetWidth === 1 ? '96' : '150'}
          grid
          grid-cols-${column ? 2 : columns.length}
          gap-0
        `}
      >
        {/* HEADER */}
        {!column &&
          columns.map((title, i) => (
            <div
              key={'pie-table-' + i}
              className={`
          w-full
          py-2
          pl-5
          border-b
          border-grey-10
          dark:border-grey-70
          ${fitMinWidth ? 'w-fit' : ' min-w-32'}
          ${
            !i &&
            widgetWidth === 2 &&
            'bg-white dark:bg-grey-90 sticky left-0 border-r'
          }
          `}
            >
              <Column
                className={`
              flex
              items-center
              w-full
              h-full
              `}
                element={
                  <Sort
                    className={`
                flex
                items-center
                text-sm
                font-medium
                w-full
                h-full
                `}
                    columnName={title}
                    key={title}
                    type={type}
                    handleSort={handleSort}
                  />
                }
                span={1}
              />
            </div>
          ))}

        {/* DATA */}
        {tableData.map(
          (
            {
              bondType,
              capital,
              color,
              coupon,
              currency,
              date,
              flow,
              issuer,
              id,
              year,
              count,
              percentage,
              value,
            },
            i
          ) => {
            const onMouseEnter = () => {
              setCenter({
                id: id!,
                value: (year ?? value ?? viewType[1] === 'CAPITAL'
                  ? capital
                  : +coupon)!,
              });
            };

            const onClick = () => {
              if (viewType.length < 2) {
                setViewType((pr) => [
                  ...pr,
                  viewType.length === 1 ? (id as 'COUPON' | 'CAPITAL') : year!,
                ]);
              }
            };

            return (
              <Fragment key={'pie-table-' + i}>
                {/* BREAKDOWN */}
                {columns?.includes('BREAKDOWN') && (
                  <div
                    className={`
                      ${fitMinWidth ? 'w-fit' : ' min-w-32'}
                      flex
                      items-center
                      pl-5
                      py-2
                      w-full
                      sticky
                      left-0
                      bg-white
                      dark:bg-grey-90
                      ${column && 'col-span-2 py-3 cursor-pointer'}
                      ${
                        center.id === id
                          ? column
                            ? viewType.length === 1
                              ? 'border'
                              : 'border-t border-l border-r'
                            : 'border-t border-l border-b'
                          : !column &&
                            'border-b border-grey-10 dark:border-grey-70'
                      }
                      ${widgetWidth === 2 && 'border-r'}
                      `}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  >
                    {color && (
                      <ColorButton
                        radius={3}
                        color={
                          Object.entries(COLORS_HEX).find(
                            ([key, col]) => col === color
                          )![0]
                        }
                        isReadOnly={true}
                      />
                    )}
                    <div className="pl-3 font-medium">{year ?? id}</div>
                  </div>
                )}

                {/* BOUNDS NUMBER */}
                {columns?.includes('COUNT') && (
                  <Container
                    center={center}
                    columnName={'Nr. of bonds'}
                    decimalScale={0}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    value={count!}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* COUPON */}
                {columns?.includes('COUPON') && (
                  <Container
                    center={center}
                    columnName={'Coupon'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    percentage={percentage}
                    symbol={symbol}
                    value={coupon!}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* CAPITAL */}
                {columns?.includes('CAPITAL') && (
                  <Container
                    center={center}
                    columnName={'Capital'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    symbol={symbol}
                    value={capital!}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* FLOW */}
                {columns?.includes('FLOW') && (
                  <Container
                    center={center}
                    columnName={'Flow'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    symbol={symbol}
                    value={flow!}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* ISSUER */}
                {columns?.includes('ISSUER') && (
                  <Container
                    center={center}
                    color={color}
                    columnName={'ISSUER'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    symbol={symbol}
                    value={issuer}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* MATURITY */}
                {columns?.includes('MATURITY') && (
                  <Container
                    center={center}
                    columnName={'MATURITY'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    symbol={symbol}
                    value={date}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* VALUE */}
                {columns?.includes('VALUE') && (
                  <Container
                    center={center}
                    columnName={'VALUE'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    symbol={symbol}
                    value={viewType[1] === 'CAPITAL' ? capital : coupon}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* CURRENCY */}
                {columns?.includes('CURRENCY') && (
                  <Container
                    center={center}
                    columnName={'CURRENCY'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    symbol={symbol}
                    value={currency}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}

                {/* BOND TYPE */}
                {columns?.includes('BOND TYPE') && (
                  <Container
                    center={center}
                    columnName={'BOND TYPE'}
                    fitMinWidth={fitMinWidth}
                    id={id}
                    isColumn={column}
                    symbol={symbol}
                    value={bondType}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  />
                )}
              </Fragment>
            );
          }
        )}
      </div>
    </>
  );
};
